import React, { useContext, useEffect, useRef, useState } from 'react';
import { Components } from './styled';
import ProjectTextField from '../../../components/inputs/ProjectTextField';
import { useQuery } from '../../../router';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import {
  createContentGroup,
  updateContentGroup,
  getContentGroupById,
  getContentGroupsByIds,
} from '../../../api/contentGroup';
import { isProjectError } from '../../../utils/errors';
import { ToastContext } from '../../../providers/ToastProvider/context';
import { ContentGroup } from '../../../api/types/contentGroup';
import { useNavigate } from 'react-router-dom';
import useModalState from '../../../hooks/useModalState';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import { confirmationTexts } from '../../../components/modals/ConfirmationModal/utils';
import { CreateContentGroupDto } from '../../../api/typeDtos/contentGroup';
import { Formik, FormikValues, FormikProps } from 'formik';
import { MinimalContentGroupSchema } from '../../../api/contentGroup/parser';
import { AppSettingsContext } from '../../../providers/AppSettingsProvider/context';
import { PURCHASE_ID_PREFIX } from '../../../defines';
import { getPastMidnightUTC } from '../../../utils/dateFormat';
import ProjectCalendarPicker from '../../../components/dateAndTime/ProjectCalendarPicker';
import ProjectContainer from '../../../components/containers/ProjectContainer';
import {
  ACCESS_TYPE,
  AccessType,
  ContentGroupType,
  Filters,
  SPECIAL_ACCESS_TYPES,
  SpecialAccessType,
  Tag,
  TAGS,
} from '../../../api/types/others';
import ProjectTextArea from '../../../components/inputs/ProjectTextArea';
import { getLessonsByIds } from '../../../api/lesson';
import { Lesson } from '../../../api/types/lesson';
import ProjectTableModal from '../../../components/ProjectTableModal';
import { modalConfigs } from '../../../components/ProjectTableModal/constants';
import { LARGE_PROJECT_MODAL_WIDTH } from '../../../utils/styled/dimensions';
import { ProjectTableDataType } from '../../../components/ProjectTable/types';
import CreateOrEditLessonModal from '../../../components/modals/CreateOrEditLessonModal';
import { getTextFromSelectedItems } from '../../../hooks/useSearchDropdown';
import ProjectSearchDropdown from '../../../components/ProjectSearchDropdown';
import { getPossibleCategories, getPossiblePillars } from '../../../api/contentManagement/utils';
import ImageFieldComponent from './components/ImageFieldComponent';
import ItemsComponent from './components/ItemsComponent';
import TableSearchBarComponent from './components/TableSearchBarComponent';
import useCustomizedDropdown from '../../../hooks/useCustomizedDropdown';
import FiltersComponent from '../../../components/FiltersComponent';
import useFiltersComponent from '../../../hooks/useFiltersComponent';
import ContentInsightsCard from '../../../components/ContentInsightsCard';
import { bulletPointPlaceholders, toBulletPointFields } from '../../../utils/bulletPoints';

const ManageContentGroup = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const initialContentGroupId = query.get('id') as string | undefined;
  const initialContentGroupType = query.get('content-group-type') as string | undefined;
  const initialAuthorId = (query.get('authorId') as string | null) || undefined;

  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { categories, pillarTypes, pillarExpertises, authors, priceTiers, goals } = useContext(AppSettingsContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const [formikValues, setFormikValues] = useState<ContentGroup>();
  const [formState, setFormState] = useState<ContentGroup>();

  const [contentGroup, setContentGroup] = useState<ContentGroup | undefined>();
  const [contentGroupType, setContentGroupType] = useState<ContentGroupType | undefined>(initialContentGroupType);

  const [possiblePillars, setPossiblePillars] = useState(pillarTypes);
  const [possibleCategories, setPossibleCategories] = useState(categories);

  const [imagePath, setImagePath] = useState<string | undefined>(contentGroup?.imagePath || undefined);
  const [displayPublishDate, setDisplayPublishDate] = useState<Date | null>(contentGroup?.publishedAtDate || null);
  const [displayReleaseDate, setDisplayReleaseDate] = useState<Date | null>(contentGroup?.releaseAtDate || null);
  const [publishDate, setPublishDate] = useState<Date | null>(contentGroup?.publishedAtDate || null);
  const [releaseDate, setReleaseDate] = useState<Date | null>(contentGroup?.releaseAtDate || null);
  const [items, setItems] = useState<(Lesson | ContentGroup)[]>([]);
  const [freeItems, setFreeItems] = useState<string[]>([]);
  const [allFilters, setAllFilters] = useState<Filters>();
  const [selectedFilters, setSelectedFilters] = useState<Filters>();

  const formikRef = useRef<FormikProps<ContentGroup>>();

  const confirmationModal = useModalState<boolean>();

  const addItemsModalProps = useModalState<{ itemsAlreadyInContentGroup?: (Lesson | ContentGroup)[] }>();
  const selectFreeItemsModalProps = useModalState<{ itemsAlreadyFree?: string[] }>();
  const editLessonModalProps = useModalState<{ lesson?: Lesson }>();

  const { props: pillarTypePropsSearch, setProps: setPillarTypePropsSearch } = useCustomizedDropdown({
    possibleValues: pillarTypes,
    singleSelect: true,
    customType: 'pillarType',
  });
  const { props: pillarExpertisePropsSearch, setProps: setPillarExpertisePropsSearch } = useCustomizedDropdown({
    possibleValues: pillarExpertises,
    singleSelect: true,
    customType: 'pillarExpertise',
  });
  const { props: categoryPropsSearch, setProps: setCategoryPropsSearch } = useCustomizedDropdown({
    possibleValues: categories,
    singleSelect: true,
  });

  const { props: categoryDropdownProps, setProps: setCategoryDropdownProps } = useCustomizedDropdown({
    possibleValues: categories,
    singleSelect: true,
  });
  const { props: pillarTypeDropdownProps, setProps: setPillarTypeDropdownProps } = useCustomizedDropdown({
    possibleValues: pillarTypes,
    singleSelect: true,
    customType: 'pillarType',
  });
  const { props: pillarExpertiseDropdownProps, setProps: setPillarExpertiseDropdownProps } = useCustomizedDropdown({
    possibleValues: pillarExpertises,
    singleSelect: true,
    customType: 'pillarExpertise',
  });
  const { props: authorDropdownProps, setProps: setAuthorDropdownProps } = useCustomizedDropdown({
    possibleValues: authors.map((item) => item?.id),
    singleSelect: true,
    customType: 'author',
  });
  const { props: accessTypeDropdownProps, setProps: setAccessTypeDropdownProps } = useCustomizedDropdown({
    possibleValues: SPECIAL_ACCESS_TYPES,
    customType: 'accessType',
  });
  const { props: priceTierDropdownProps, setProps: setPriceTierDropdownProps } = useCustomizedDropdown({
    possibleValues: priceTiers.map((item) => item?.productId),
    customType: 'priceTier',
    singleSelect: true,
  });

  const [tagsChoices, setTagsChoices] = useState<Tag[]>(TAGS);
  const { props: tagsDropdownProps, setProps: setTagsDropdownProps } = useCustomizedDropdown({
    possibleValues: tagsChoices.map((item) => item?.value),
    customType: 'tag',
  });

  const defaultFiltersProps = useFiltersComponent({ pillarTypes, goals, categories: possibleCategories });
  const disabledFiltersProps = useFiltersComponent({ pillarTypes, goals, categories: possibleCategories });

  const fetchContentGroup = async (id?: string) => {
    const _id = initialContentGroupId || id;
    if (_id) {
      const contentGroupData = await getContentGroupById(_id);
      if (!!contentGroupData) {
        setContentGroup(contentGroupData as any);
      }
    }
  };

  useEffect(() => {
    fetchContentGroup().then();
  }, [initialContentGroupId]);

  // Prefill the author when arriving from the author page to create a new course.
  useEffect(() => {
    if (!initialContentGroupId && initialAuthorId) {
      setAuthorDropdownProps([initialAuthorId]);
    }
  }, [initialContentGroupId, initialAuthorId]);

  useEffect(() => {
    if (contentGroup) {
      setContentGroupType(contentGroup?.type);
      updateForm(contentGroup).then();
      if (initialContentGroupId !== contentGroup.id) {
        navigate(`/content-groups/manage?id=${contentGroup.id}`);
      }
    }
  }, [contentGroup]);

  useEffect(() => {
    if (Array.isArray(contentGroup?.tags) && contentGroup?.tags?.length) {
      const allTags = TAGS;
      contentGroup?.tags?.forEach((value) => {
        const matchingTag = TAGS.filter((item) => item.value === value)?.[0];
        if (!matchingTag) {
          allTags.push({ value: value, text: value });
        }
      });
      setTagsChoices(allTags);
    }
  }, [contentGroup?.tags]);

  useEffect(() => {
    const didLoad = pillarTypes.length > 0 && categories.length > 0;
    if (didLoad) {
      const selectedPillar = pillarTypeDropdownProps.selectedItems?.[0];
      if (!!selectedPillar?.value) {
        setPossibleCategories(getPossibleCategories(goals, selectedPillar?.value));
      } else {
        setPossibleCategories(categories);
      }
    }
  }, [categories, pillarTypes, pillarTypeDropdownProps.selectedItems]);

  useEffect(() => {
    const didLoad = pillarTypes.length > 0 && categories.length > 0;
    if (didLoad) {
      const selectedCategory = categoryDropdownProps.selectedItems?.[0];
      if (!!selectedCategory?.value) {
        setPossiblePillars(getPossiblePillars(goals, selectedCategory?.value));
      } else {
        setPossiblePillars(pillarTypes);
      }
    }
  }, [categories, pillarTypes, categoryDropdownProps.selectedItems]);

  useEffect(() => {
    setFormState({
      id: contentGroup?.id || '',
      title: formikValues?.title ? formikValues?.title : contentGroup?.title || '',
      createdAtDate: contentGroup?.createdAtDate || new Date(),
      updatedAtDate: contentGroup?.updatedAtDate || new Date(),
      ...formikValues,
      imagePath,
      packId: contentGroup?.packId,
      category: categoryDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      pillarType: pillarTypeDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      pillarExpertise: pillarExpertiseDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
    });
  }, [
    formikValues,
    imagePath,
    categoryDropdownProps.selectedItems,
    pillarTypeDropdownProps.selectedItems,
    pillarExpertiseDropdownProps.selectedItems,
  ]);

  const refreshSearchBarProps = () => {
    setPillarTypePropsSearch([]);
    setPillarExpertisePropsSearch([]);
    setCategoryPropsSearch([]);
  };

  const updateForm = async (contentGroup: ContentGroup) => {
    setIsLoading(true);
    const _contentGroupType = contentGroup?.type || contentGroupType;
    const newForm = {
      id: contentGroup?.id,
      title: contentGroup?.title,
      type: _contentGroupType,
      description: contentGroup?.description,
      purchaseId: contentGroup?.purchaseId?.replace(PURCHASE_ID_PREFIX.contentGroup, ''),
      createdAtDate: contentGroup?.createdAtDate,
      updatedAtDate: contentGroup?.updatedAtDate,
      removeFromSale: contentGroup?.removeFromSale || false,
      isTestingGroup: contentGroup?.isTestingGroup || false,
      bulletPoints: toBulletPointFields(contentGroup?.bulletPoints),
      pathGoal: contentGroup?.pathGoal || '',
    };
    formikRef?.current?.setValues(newForm);
    setImagePath(contentGroup?.imagePath);
    setDisplayPublishDate(contentGroup?.publishedAtDate || null);
    setPublishDate(contentGroup?.publishedAtDate || null);
    setDisplayReleaseDate(contentGroup?.releaseAtDate || null);
    setReleaseDate(contentGroup?.releaseAtDate || null);
    if (_contentGroupType === 'collection') {
      setItems(await getLessonsByIds(contentGroup?.itemIds || []));
    } else if (_contentGroupType === 'bundle') {
      setItems(await getContentGroupsByIds(contentGroup?.itemIds || []));
    }
    setFreeItems(contentGroup?.freeItemIds || []);
    if (contentGroup?.category && contentGroup?.category?.length > 0) {
      setCategoryDropdownProps([contentGroup?.category]);
    }
    if (contentGroup?.pillarType && contentGroup?.pillarType?.length > 0) {
      setPillarTypeDropdownProps([contentGroup?.pillarType]);
    }
    if (contentGroup?.pillarExpertise && contentGroup?.pillarExpertise?.length > 0) {
      setPillarExpertiseDropdownProps([contentGroup?.pillarExpertise]);
    }
    if (contentGroup?.authorId && contentGroup?.authorId?.length > 0) {
      setAuthorDropdownProps([contentGroup?.authorId]);
    }
    if (contentGroup?.specialAccessType && contentGroup?.specialAccessType?.length) {
      setAccessTypeDropdownProps([contentGroup?.specialAccessType]);
    }
    if (contentGroup?.priceTier && contentGroup?.priceTier?.length > 0) {
      setPriceTierDropdownProps([contentGroup?.priceTier]);
    }
    setTagsDropdownProps(contentGroup?.tags || []);
    if (contentGroup.type === 'bundle') {
      if (!!contentGroup?.filters?.defaultFilters) {
        defaultFiltersProps.setDropdownProps(contentGroup?.filters?.defaultFilters);
      }
      if (!!contentGroup?.filters?.disabledFilters) {
        disabledFiltersProps.setDropdownProps(contentGroup?.filters?.disabledFilters);
      }
      if (!!contentGroup?.filters?.allFilters) {
        setAllFilters(contentGroup?.filters?.allFilters);
      }
      if (!!contentGroup?.filters?.selectedFilters) {
        setSelectedFilters(contentGroup?.filters?.selectedFilters);
      }
    }
    setIsLoading(false);
  };

  const onClearPublishDate = () => {
    setPublishDate(null);
    setDisplayPublishDate(null);
  };

  const onClearReleaseDate = () => {
    setReleaseDate(null);
    setDisplayReleaseDate(null);
  };

  const onSelectDateFromCalendar = (value: Date | null) => {
    if (value !== null) {
      setDisplayPublishDate(value);
      const utcDate = new Date(getPastMidnightUTC(value) + 24 * 60 * 60 * 1000);
      setPublishDate(utcDate);
    }
  };

  const onSelectReleaseDateFromCalendar = (value: Date | null) => {
    if (value !== null) {
      setDisplayReleaseDate(value);
      const utcDate = new Date(getPastMidnightUTC(value));
      setReleaseDate(utcDate);
    }
  };
  const onSelectFreeItems = async (data: ProjectTableDataType | string[]) => {
    const itemsAlreadyFree = selectFreeItemsModalProps?.modalState?.itemsAlreadyFree;
    const newItems = data as (Lesson | ContentGroup)[] | undefined;
    if (newItems && Array.isArray(newItems)) {
      setIsLoading(true);
      let itemIds;
      if (itemsAlreadyFree && Array.isArray(itemsAlreadyFree)) {
        itemIds = itemsAlreadyFree.concat(newItems.map((item) => item?.id));
      } else {
        itemIds = newItems.map((item) => item?.id);
      }
      setFreeItems(itemIds);
      setIsLoading(false);
    }
    selectFreeItemsModalProps.onModalClose();
  };

  const onAddItems = async (data: ProjectTableDataType | string[]) => {
    const itemsAlreadyInContentGroup = addItemsModalProps?.modalState?.itemsAlreadyInContentGroup as (
      | Lesson
      | ContentGroup
    )[];
    const newItems = data as (Lesson | ContentGroup)[] | undefined;
    if (newItems && Array.isArray(newItems)) {
      setIsLoading(true);
      let _items;
      if (itemsAlreadyInContentGroup && Array.isArray(itemsAlreadyInContentGroup)) {
        _items = itemsAlreadyInContentGroup.concat(newItems);
      } else {
        _items = newItems;
      }
      setItems(_items);
      setIsLoading(false);
    }
    addItemsModalProps.onModalClose();
  };

  const onEditItem = (id: string | undefined) => {
    if (!!id) {
      const foundItem = items?.find((item) => item?.id === id);
      if (foundItem) {
        switch (contentGroupType) {
          case 'collection':
            editLessonModalProps.open({ lesson: foundItem as Lesson });
            break;
          case 'bundle':
            console.log('here');
            navigate(`/content-groups/manage?id=${id}`);
            break;
        }
      }
    }
  };

  const onRemoveItem = (id: string | undefined) => {
    if (!!id) {
      setIsLoading(true);
      const newItems = items.filter((item) => item?.id !== id);
      const newFreeItems = freeItems.filter((itemId) => itemId !== id);
      setItems(newItems);
      setFreeItems(newFreeItems);
      setIsLoading(false);
    }
  };

  const onRemoveFreeItem = (id: string | undefined) => {
    if (!!id) {
      setIsLoading(true);
      const newFreeItems = freeItems.filter((item) => item !== id);
      setFreeItems(newFreeItems);
      setIsLoading(false);
    }
  };

  const onMoveUpItem = (id: string | undefined) => {
    if (!!id) {
      if (items[0]?.id === id) {
        return;
      }
      setIsLoading(true);
      let selectedRowIndex = 0;
      for (let index = 1; index < items.length; index++) {
        if (items[index]?.id === id) {
          selectedRowIndex = index;
          break;
        }
      }
      const newItems = items.map((item) => ({ ...item }));
      newItems[selectedRowIndex - 1] = items[selectedRowIndex];
      newItems[selectedRowIndex] = items[selectedRowIndex - 1];
      setItems(newItems);
      setIsLoading(false);
    }
  };

  const onMoveDownItem = (id: string | undefined) => {
    if (!!id) {
      if (items[items.length - 1]?.id === id) {
        return;
      }
      setIsLoading(true);
      let selectedRowIndex = -1;
      for (let index = 0; index < items.length - 1; index++) {
        if (items[index]?.id === id) {
          selectedRowIndex = index;
          break;
        }
      }
      const newItems = items.map((item) => ({ ...item }));
      newItems[selectedRowIndex + 1] = items[selectedRowIndex];
      newItems[selectedRowIndex] = items[selectedRowIndex + 1];
      setItems(newItems);
      setIsLoading(false);
    }
  };

  const onCreateOrEditModalClose = (refreshTrigger?: boolean) => {
    if (refreshTrigger) {
      fetchContentGroup?.().then();
    }
    editLessonModalProps.onModalClose();
  };

  const onCancel = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const processAccessType = (accessType: string | undefined) => {
    return accessType && accessType?.length
      ? accessType === 'yearlySubscriptionOnly'
        ? ['purchase']
        : ACCESS_TYPE.includes(accessType)
        ? [accessType]
        : ['noPayment']
      : ['subscription'];
  };

  const _onSubmit = async (values: FormikValues) => {
    const _specialAccessType = accessTypeDropdownProps?.selectedItems?.[0]?.value;
    const _accessTypes = processAccessType(_specialAccessType);

    await _onSave?.({
      ...values,
      type: contentGroupType,
      publishDate: publishDate,
      releaseDate: releaseDate,
      imagePath: imagePath,
      items: items,
      freeItems: freeItems,
      specialAccessType: _specialAccessType,
      ...(categoryDropdownProps?.selectedItems?.length > 0 && {
        category: categoryDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      }),
      ...(pillarTypeDropdownProps?.selectedItems?.length > 0 && {
        pillarType: pillarTypeDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      }),
      ...(pillarExpertiseDropdownProps?.selectedItems?.length > 0 && {
        pillarExpertise: pillarExpertiseDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      }),
      ...(authorDropdownProps?.selectedItems?.length > 0 && {
        authorId: authorDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
        //? releaseAt?
      }),
      ...(_accessTypes && {
        accessType: _accessTypes,
      }),
      ...(priceTierDropdownProps?.selectedItems?.length > 0 && {
        priceTier: priceTierDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
      }),
      ...(tagsDropdownProps?.selectedItems?.length > 0 && {
        tags: tagsDropdownProps?.selectedItems?.map((item) => item.value),
      }),
      ...(contentGroupType === 'bundle' && {
        filters: {
          defaultFilters: defaultFiltersProps.getDropdownProps(),
          disabledFilters: disabledFiltersProps.getDropdownProps(),
          ...(!!allFilters && { allFilters }),
          ...(!!selectedFilters && { selectedFilters }),
        },
      }),
    });
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreateContentGroupDto = {
      title: values?.title,
      type: values?.type,
      category: values?.category?.length ? values?.category : null,
      pillarType: values?.pillarType?.length ? values?.pillarType : null,
      pillarExpertise: values?.pillarExpertise?.length ? values?.pillarExpertise : null,
      publishedAt: values?.publishDate ? values?.publishDate?.getTime() : null,
      releaseAt: values?.releaseDate ? values?.releaseDate?.getTime() : null,
      imagePath: values?.imagePath?.length ? values?.imagePath : null,
      description: values?.description?.length ? values?.description : null,
      priceTier: values?.priceTier?.length ? values?.priceTier : null,
      purchaseId: values?.purchaseId?.length ? ((PURCHASE_ID_PREFIX.contentGroup + values?.purchaseId) as any) : null,
      itemIds: values?.items && Array.isArray(values?.items) ? values?.items?.map((item) => item?.id) : [],
      freeItemIds: values?.freeItems?.length ? values?.freeItems : [],
      accessType:
        values?.type === 'bundle'
          ? ['noPayment']
          : values?.accessType && Array.isArray(values?.accessType)
          ? (values?.accessType as AccessType[])
          : [],
      specialAccessType:
        values?.type === 'bundle'
          ? ('noPayment' as SpecialAccessType)
          : values?.specialAccessType && values?.specialAccessType?.length
          ? (values?.specialAccessType as SpecialAccessType)
          : undefined,
      authorId: values?.authorId?.length ? values?.authorId : null,
      removeFromSale: !!values?.removeFromSale,
      isTestingGroup: !!values?.isTestingGroup,
      ...(!!values.filters && { filters: values.filters }),
      tags: values?.tags || [],
      ...(values?.type === 'collection' && {
        bulletPoints: (values?.bulletPoints || []).map((b: string) => (b || '').trim()).filter(Boolean),
        // Empty string removes nothing on update, so send null to clear it.
        pathGoal: (values?.pathGoal || '').trim() || null,
      }),
    };

    setIsLoading(true);
    const result =
      contentGroup && contentGroup?.id
        ? await updateContentGroup({ ...data, id: contentGroup?.id })
        : await createContentGroup(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      showSuccessToast(
        contentGroup && contentGroup?.id
          ? 'The changes were saved successfully'
          : 'You successfully added a new ' + contentGroupType,
      );
      if ('id' in result && result?.id) {
        await fetchContentGroup((result as any)?.id);
      }
    }
  };

  const onConfirmCancel = () => {
    navigate(-1);
  };

  const formikInitialValues: ContentGroup = {
    id: '',
    title: '',
    type: contentGroupType,
    createdAtDate: new Date(),
    updatedAtDate: new Date(),
    removeFromSale: false,
    isTestingGroup: false,
    bulletPoints: toBulletPointFields(),
    pathGoal: '',
  };

  return (
    <ProjectContainer
      title={contentGroup ? `Edit ${contentGroupType}` : `Create new ${contentGroupType}`}
      secondaryTitle={contentGroup ? `ID: ${contentGroup.id}` : ''}>
      <Components.ModalContentContainer>
        <Formik
          innerRef={(f) => {
            if (!!f) {
              formikRef.current = f;
            }
          }}
          initialValues={formikInitialValues}
          validationSchema={MinimalContentGroupSchema}
          onSubmit={_onSubmit}>
          {({ values, handleChange, errors, touched }) => {
            setFormikValues(values);
            return (
              <Components.Form>
                <Components.ModalBodyContainer>
                  <ContentInsightsCard content={formState} contentType={'contentGroup'} cardStyle={'detailed'} />
                  <Components.FieldsWrapper>
                    <Components.FieldStartAdornment variant={'minimal'} />
                    <Components.FieldsContent>
                      <Components.FieldWrapper>
                        <Components.FieldLabel>Title</Components.FieldLabel>
                        <ProjectTextField
                          value={values.title}
                          onChangeText={handleChange}
                          name={'title'}
                          errorMessage={errors.title && touched.title ? errors?.title : undefined}
                          placeholder={`Enter ${contentGroupType} title`}
                        />
                      </Components.FieldWrapper>
                    </Components.FieldsContent>
                  </Components.FieldsWrapper>

                  <Components.FieldsWrapper>
                    <Components.FieldStartAdornment variant={'additional'} />
                    <Components.FieldsContent>
                      <Components.FieldWrapper>
                        <Components.FieldWrapperWithInsight>
                          <Components.FieldLabel>Image</Components.FieldLabel>
                          <ContentInsightsCard
                            content={formState}
                            fieldName={'imagePath'}
                            contentType={'contentGroup'}
                            cardStyle={'fieldAdornment'}
                          />
                        </Components.FieldWrapperWithInsight>
                        <ImageFieldComponent imagePath={imagePath} setImagePath={setImagePath} />
                      </Components.FieldWrapper>

                      <Components.FieldWrapper>
                        <Components.FieldLabel>Description</Components.FieldLabel>
                        <ProjectTextArea
                          value={values.description}
                          onChangeText={handleChange}
                          name={'description'}
                          errorMessage={errors.description && touched.description ? errors?.description : undefined}
                          placeholder={`Enter ${contentGroupType} description`}
                        />
                      </Components.FieldWrapper>

                      {contentGroupType === 'collection' &&
                        values.bulletPoints?.map((bulletPoint: string, index: number) => (
                          <Components.FieldWrapper key={index}>
                            <Components.FieldLabel>Bullet point {index + 1}</Components.FieldLabel>
                            <ProjectTextField
                              value={bulletPoint}
                              onChangeText={handleChange}
                              name={`bulletPoints[${index}]`}
                              placeholder={bulletPointPlaceholders[index]}
                            />
                          </Components.FieldWrapper>
                        ))}

                      {contentGroupType === 'collection' && (
                        <Components.FieldWrapper>
                          <Components.FieldLabel>Path goal</Components.FieldLabel>
                          <ProjectTextField
                            value={values.pathGoal || ''}
                            onChangeText={handleChange}
                            name={'pathGoal'}
                            placeholder={'e.g. Scaling to $10M'}
                          />
                          <Components.FieldBasic>
                            Shown in the path&apos;s final MASTER row (e.g. &apos;Scaling to $10M&apos;). Leave empty
                            for &apos;Path mastery&apos;.
                          </Components.FieldBasic>
                        </Components.FieldWrapper>
                      )}

                      <Components.FieldWrapper>
                        <Components.FieldWrapperWithInsight>
                          <Components.FieldLabel>Category</Components.FieldLabel>
                          <ContentInsightsCard
                            content={formState}
                            fieldName={'category'}
                            contentType={'contentGroup'}
                            cardStyle={'fieldAdornment'}
                          />
                        </Components.FieldWrapperWithInsight>
                        <Components.Picker onClick={(e) => categoryDropdownProps.show(e)}>
                          {categoryDropdownProps?.selectedItems && categoryDropdownProps?.selectedItems?.length > 0 ? (
                            <Components.PickerLabel>
                              {getTextFromSelectedItems(categoryDropdownProps?.selectedItems)}
                            </Components.PickerLabel>
                          ) : (
                            <Components.NotSetLabel>Not set</Components.NotSetLabel>
                          )}
                          <Components.PickerIconWrapper>
                            <Components.PickerIcon />
                          </Components.PickerIconWrapper>
                        </Components.Picker>
                        <ProjectSearchDropdown
                          {...categoryDropdownProps}
                          facetFilter={(item) => possibleCategories.includes(item.value)}
                        />
                      </Components.FieldWrapper>

                      <Components.FieldWrapper>
                        <Components.LengthInputsWrapper>
                          <Components.FieldWrapperWithInsight>
                            <Components.FieldLabel>Pillar</Components.FieldLabel>
                            <ContentInsightsCard
                              content={formState}
                              fieldName={'pillarType'}
                              contentType={'contentGroup'}
                              cardStyle={'fieldAdornment'}
                            />
                          </Components.FieldWrapperWithInsight>

                          <Components.FieldWrapperWithInsight>
                            <Components.FieldLabel>Expertise</Components.FieldLabel>
                            <ContentInsightsCard
                              content={formState}
                              fieldName={'pillarExpertise'}
                              contentType={'contentGroup'}
                              cardStyle={'fieldAdornment'}
                            />
                          </Components.FieldWrapperWithInsight>
                        </Components.LengthInputsWrapper>

                        <Components.LengthInputsWrapper>
                          <>
                            <Components.Picker onClick={(e) => pillarTypeDropdownProps.show(e)}>
                              {pillarTypeDropdownProps?.selectedItems &&
                              pillarTypeDropdownProps?.selectedItems?.length > 0 ? (
                                <Components.PickerLabel>
                                  {getTextFromSelectedItems(pillarTypeDropdownProps?.selectedItems)}
                                </Components.PickerLabel>
                              ) : (
                                <Components.NotSetLabel>Not set</Components.NotSetLabel>
                              )}
                              <Components.PickerIconWrapper>
                                <Components.PickerIcon />
                              </Components.PickerIconWrapper>
                            </Components.Picker>
                            <ProjectSearchDropdown
                              {...pillarTypeDropdownProps}
                              facetFilter={(item) => possiblePillars.includes(item.value)}
                            />
                          </>
                          <Components.Picker onClick={(e) => pillarExpertiseDropdownProps.show(e)}>
                            {pillarExpertiseDropdownProps?.selectedItems &&
                            pillarExpertiseDropdownProps?.selectedItems?.length > 0 ? (
                              <Components.PickerLabel>
                                {getTextFromSelectedItems(pillarExpertiseDropdownProps?.selectedItems)}
                              </Components.PickerLabel>
                            ) : (
                              <Components.NotSetLabel>Not set</Components.NotSetLabel>
                            )}
                            <Components.PickerIconWrapper>
                              <Components.PickerIcon />
                            </Components.PickerIconWrapper>
                          </Components.Picker>
                          <ProjectSearchDropdown {...pillarExpertiseDropdownProps} />
                          <></>
                        </Components.LengthInputsWrapper>
                      </Components.FieldWrapper>

                      {contentGroupType === 'collection' && (
                        <Components.FieldWrapper>
                          <Components.FieldLabel>Tags</Components.FieldLabel>
                          <Components.Picker onClick={(e) => tagsDropdownProps.show(e)}>
                            {tagsDropdownProps?.selectedItems && tagsDropdownProps?.selectedItems?.length > 0 ? (
                              <Components.PickerLabel>
                                {getTextFromSelectedItems(tagsDropdownProps?.selectedItems)}
                              </Components.PickerLabel>
                            ) : (
                              <Components.NotSetLabel>Not set</Components.NotSetLabel>
                            )}
                            <Components.PickerIconWrapper>
                              <Components.PickerIcon />
                            </Components.PickerIconWrapper>
                          </Components.Picker>
                          <ProjectSearchDropdown {...tagsDropdownProps} />
                        </Components.FieldWrapper>
                      )}

                      {contentGroupType && (
                        <ItemsComponent
                          contentGroupType={contentGroupType}
                          items={items}
                          freeItems={freeItems}
                          onEditItem={onEditItem}
                          onAddItems={() => {
                            refreshSearchBarProps();
                            addItemsModalProps.open({ itemsAlreadyInContentGroup: items });
                          }}
                          onRemoveItem={onRemoveItem}
                          onAddFreeItems={() => {
                            refreshSearchBarProps();
                            selectFreeItemsModalProps.open({ itemsAlreadyFree: freeItems });
                          }}
                          onRemoveFreeItem={onRemoveFreeItem}
                          onMoveUpItem={onMoveUpItem}
                          onMoveDownItem={onMoveDownItem}
                        />
                      )}

                      {contentGroupType === 'collection' && (
                        <Components.FieldWrapper>
                          <Components.FieldLabel>Author</Components.FieldLabel>
                          <Components.Picker onClick={(e) => authorDropdownProps.show(e)}>
                            {authorDropdownProps?.selectedItems && authorDropdownProps?.selectedItems?.length > 0 ? (
                              <Components.PickerLabel>
                                {getTextFromSelectedItems(authorDropdownProps?.selectedItems)}
                              </Components.PickerLabel>
                            ) : (
                              <Components.NotSetLabel>Not set</Components.NotSetLabel>
                            )}
                            <Components.PickerIconWrapper>
                              <Components.PickerIcon />
                            </Components.PickerIconWrapper>
                          </Components.Picker>
                          <ProjectSearchDropdown {...authorDropdownProps} />
                        </Components.FieldWrapper>
                      )}

                      <Components.FieldWrapper>
                        <Components.FieldLabel>Purchase ID</Components.FieldLabel>
                        <Components.PrefixedTextFieldWrapper>
                          <Components.PrefixLabel>{PURCHASE_ID_PREFIX.contentGroup}</Components.PrefixLabel>
                          <ProjectTextField
                            value={values?.purchaseId?.replace(PURCHASE_ID_PREFIX.contentGroup, '')}
                            onChangeText={handleChange}
                            name={'purchaseId'}
                            errorMessage={errors.purchaseId && touched.purchaseId ? errors?.purchaseId : undefined}
                            placeholder={`Enter a suggestive suffix for the ${contentGroupType} purchase ID`}
                          />
                        </Components.PrefixedTextFieldWrapper>
                      </Components.FieldWrapper>

                      {contentGroupType === 'collection' ? (
                        <>
                          <Components.FieldWrapper>
                            <Components.FieldLabel>Special access type</Components.FieldLabel>
                            <Components.CheckboxDescription>
                              {`For versions before v2.0, this access type will be saved as "${
                                processAccessType(accessTypeDropdownProps?.selectedItems?.[0]?.value)?.[0]
                              }"`}
                              <br />
                              {'Set it only for special occasions. If left empty, we will set it as "subscription"'}
                            </Components.CheckboxDescription>
                            <Components.Picker onClick={(e) => accessTypeDropdownProps.show(e)}>
                              {accessTypeDropdownProps?.selectedItems &&
                              accessTypeDropdownProps?.selectedItems?.length > 0 ? (
                                <Components.PickerLabel>
                                  {getTextFromSelectedItems(accessTypeDropdownProps?.selectedItems)}
                                </Components.PickerLabel>
                              ) : (
                                <Components.NotSetLabel>Not set</Components.NotSetLabel>
                              )}
                              <Components.PickerIconWrapper>
                                <Components.PickerIcon />
                              </Components.PickerIconWrapper>
                            </Components.Picker>
                            <ProjectSearchDropdown {...accessTypeDropdownProps} />
                          </Components.FieldWrapper>

                          <Components.FieldWrapper>
                            <Components.FieldLabel>Price tier</Components.FieldLabel>
                            <Components.Picker onClick={(e) => priceTierDropdownProps.show(e)}>
                              {priceTierDropdownProps?.selectedItems &&
                              priceTierDropdownProps?.selectedItems?.length > 0 ? (
                                <Components.PickerLabel>
                                  {getTextFromSelectedItems(priceTierDropdownProps?.selectedItems)}
                                </Components.PickerLabel>
                              ) : (
                                <Components.NotSetLabel>Not set</Components.NotSetLabel>
                              )}
                              <Components.PickerIconWrapper>
                                <Components.PickerIcon />
                              </Components.PickerIconWrapper>
                            </Components.Picker>
                            <ProjectSearchDropdown {...priceTierDropdownProps} />
                          </Components.FieldWrapper>
                        </>
                      ) : (
                        <>
                          <Components.FieldLabel>No payment</Components.FieldLabel>
                          <Components.BasicText align={'left'}>
                            Bundles cannot be purchased. The access type, visibility restriction and price tier fields
                            are not customisable in the case of bundles.
                          </Components.BasicText>
                        </>
                      )}

                      <Components.FieldWrapper>
                        <Components.FieldLabel>Published at</Components.FieldLabel>
                        <ProjectCalendarPicker
                          dateValue={displayPublishDate}
                          onDateValueChange={onSelectDateFromCalendar}
                          valueDisplayFormat={'day'}
                        />
                        {publishDate && (
                          <Components.ClearButton onClick={() => onClearPublishDate()}>
                            Clear publish date
                          </Components.ClearButton>
                        )}
                      </Components.FieldWrapper>

                      <Components.FieldWrapper>
                        <Components.FieldLabel>Release date</Components.FieldLabel>
                        <ProjectCalendarPicker
                          dateValue={displayReleaseDate}
                          onDateValueChange={onSelectReleaseDateFromCalendar}
                          valueDisplayFormat={'day'}
                        />
                        {releaseDate && (
                          <Components.ClearButton onClick={() => onClearReleaseDate()}>
                            Clear release date
                          </Components.ClearButton>
                        )}
                      </Components.FieldWrapper>

                      <Components.FieldWrapper>
                        <Components.FieldLabel>Special content group tags</Components.FieldLabel>
                        <Components.CheckboxFieldWrapper>
                          <Components.Checkbox
                            destructive={true}
                            checked={values?.removeFromSale || false}
                            onChange={handleChange}
                            name={'removeFromSale'}
                          />
                          <Components.CheckboxLabel destructive={values?.removeFromSale || false}>
                            Remove from sale
                          </Components.CheckboxLabel>
                          <Components.CheckboxDescription destructive={values?.removeFromSale || false}>
                            If checked, this {contentGroupType || ''} will not appear in-app. This should only be used
                            as a last resort.
                            <br />
                            For other cases, please consider removing the publish date.
                          </Components.CheckboxDescription>
                          <ContentInsightsCard
                            content={formState}
                            fieldName={'removeFromSale'}
                            contentType={'contentGroup'}
                            cardStyle={'fieldAdornment'}
                          />
                        </Components.CheckboxFieldWrapper>

                        <Components.CheckboxFieldWrapper>
                          <Components.Checkbox
                            checked={values?.isTestingGroup || false}
                            onChange={handleChange}
                            name={'isTestingGroup'}
                          />
                          <Components.CheckboxLabel>Is testing group</Components.CheckboxLabel>
                          <Components.CheckboxDescription>
                            If checked, this {contentGroupType || ''} will only appear to testers, inside testing
                            sections.
                          </Components.CheckboxDescription>
                          <ContentInsightsCard
                            content={formState}
                            fieldName={'isTestingGroup'}
                            contentType={'contentGroup'}
                            cardStyle={'fieldAdornment'}
                          />
                        </Components.CheckboxFieldWrapper>
                      </Components.FieldWrapper>

                      {contentGroupType === 'bundle' && (
                        <>
                          <Components.FiltersNameWrapper>Default Filters</Components.FiltersNameWrapper>
                          <FiltersComponent filtersDropdownProps={defaultFiltersProps.dropdownProps} />
                          <Components.FiltersNameWrapper>Disabled Filters</Components.FiltersNameWrapper>
                          <FiltersComponent filtersDropdownProps={disabledFiltersProps.dropdownProps} />
                          <Components.FiltersWrapper>
                            <Components.BasicText align={'right'}>
                              Note: the category options that are visible for these filters are restricted by the bundle
                              {"'"}s selected pillar.
                            </Components.BasicText>
                          </Components.FiltersWrapper>
                        </>
                      )}
                    </Components.FieldsContent>
                  </Components.FieldsWrapper>
                </Components.ModalBodyContainer>
                <Components.ModalButtonsContainer>
                  <ContentInsightsCard content={formState} contentType={'contentGroup'} cardStyle={'small'} />
                  <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
                  <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
                </Components.ModalButtonsContainer>
              </Components.Form>
            );
          }}
        </Formik>
      </Components.ModalContentContainer>
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.contentGroups.cancelEditing}
        onConfirm={onConfirmCancel}
      />
      {contentGroupType === 'collection' && (
        <CreateOrEditLessonModal {...editLessonModalProps} onModalClose={onCreateOrEditModalClose} />
      )}
      {contentGroupType === 'collection' && (
        <>
          <ProjectTableModal
            modalConfig={modalConfigs.selectLessons({
              category: categoryPropsSearch.selectedItems?.[0]?.value,
              pillarType: pillarTypePropsSearch.selectedItems?.[0]?.value,
              pillarExpertise: pillarExpertisePropsSearch.selectedItems?.[0]?.value,
            })}
            {...addItemsModalProps}
            ignoredIds={items.map((item) => item?.id)}
            onModalSubmit={onAddItems}
            modalTitle={'Select lessons to add in this collection'}
            modalWidth={LARGE_PROJECT_MODAL_WIDTH}
            tableSearchChildren={
              <TableSearchBarComponent
                pillarTypeProps={pillarTypePropsSearch}
                pillarExpertiseProps={pillarExpertisePropsSearch}
                categoryProps={categoryPropsSearch}
              />
            }
            sortingModel={{ field: 'publishedAt', sort: 'desc' }}
          />
          <ProjectTableModal
            modalConfig={modalConfigs.selectLessons({
              category: categoryPropsSearch.selectedItems?.[0]?.value,
              pillarType: pillarTypePropsSearch.selectedItems?.[0]?.value,
              pillarExpertise: pillarExpertisePropsSearch.selectedItems?.[0]?.value,
            })}
            {...selectFreeItemsModalProps}
            ignoredIds={freeItems}
            keepIds={items.map((item) => item?.id)}
            onModalSubmit={onSelectFreeItems}
            modalTitle={'Select lessons to be marked as free in this collection'}
            modalWidth={LARGE_PROJECT_MODAL_WIDTH}
            tableSearchChildren={
              <TableSearchBarComponent
                pillarTypeProps={pillarTypePropsSearch}
                pillarExpertiseProps={pillarExpertisePropsSearch}
                categoryProps={categoryPropsSearch}
              />
            }
            sortingModel={{ field: 'publishedAt', sort: 'desc' }}
          />
        </>
      )}
      {contentGroupType === 'bundle' && (
        <>
          <ProjectTableModal
            modalConfig={modalConfigs.selectCollections({
              category: categoryPropsSearch.selectedItems?.[0]?.value,
              pillarType: pillarTypePropsSearch.selectedItems?.[0]?.value,
              pillarExpertise: pillarExpertisePropsSearch.selectedItems?.[0]?.value,
            })}
            {...addItemsModalProps}
            ignoredIds={items.map((item) => item?.id)}
            onModalSubmit={onAddItems}
            modalTitle={'Select collections to add in this bundle'}
            modalWidth={LARGE_PROJECT_MODAL_WIDTH}
            tableSearchChildren={
              <TableSearchBarComponent
                pillarTypeProps={pillarTypePropsSearch}
                pillarExpertiseProps={pillarExpertisePropsSearch}
                categoryProps={categoryPropsSearch}
              />
            }
            sortingModel={{ field: 'publishedAt', sort: 'desc' }}
          />
        </>
      )}
    </ProjectContainer>
  );
};

export default ManageContentGroup;
